export const VC_DAY_SEQUENCE = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

export const VC_DAY_LABELS = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
};

export const VECTOR_PRESET_ORDER = ['conservative', 'balanced', 'aggressive'];

export const VECTOR_PRESET_META = {
  conservative: {
    label: 'Conservative',
    riskLabel: 'Approx. 1% risk/trade',
    description: 'Smallest preset exposure bucket, scaled off the selected capital base.',
  },
  balanced: {
    label: 'Balanced',
    riskLabel: 'Approx. 2.5% risk/trade',
    description: 'Middle preset exposure bucket, scaled off the selected capital base.',
  },
  aggressive: {
    label: 'Aggressive',
    riskLabel: 'Approx. 5% risk/trade',
    description: 'Standard preset, scaled off the selected capital base. This is the default selection.',
  },
  equity_portfolio_1x: {
    label: 'Equity Portfolio 1×',
    riskLabel: 'Equal-weight · compounded · 1.0× leverage',
    description:
      'Equal-weight blend of the five 1-hour stock algos — AAPL, TSLA (NASDAQ feed), NVDA, AMZN, PLTR — at 20% each. '
      + 'Profits are reinvested (compounded), so the curve grows on the running balance, not a fixed base.',
  },
  equity_portfolio_225x: {
    label: 'Equity Portfolio 2.25×',
    riskLabel: 'Equal-weight · compounded · 2.25× leverage',
    description:
      'Same equal-weight 5-stock blend as Equity Portfolio 1× (20% each), levered 2.25× (45% per stock). '
      + 'The leverage model is admin-selectable: Fyntrak-linear (2.25× the 1× annualized — the default) or real '
      + 'compounded leverage (which grows faster than a flat 2.25× of the 1× curve).',
  },
  og_equity_portfolio_1x: {
    label: 'OG Equity Portfolio 1×',
    riskLabel: 'Fyntrak mirror · 1.0× leverage',
    description:
      'Fyntrak\'s ingested 1× history through the last export date, then equal-weight OG stock legs '
      + '(AAPL, TSLA PYTH, NVDA, AMZN, PLTR) stitched live from the OG webhook feed. Historical '
      + 'points before the stitch are unchanged from Fyntrak.',
  },
  og_equity_portfolio_225x: {
    label: 'OG Equity Portfolio 2.25×',
    riskLabel: 'Fyntrak mirror · 2.25× leverage',
    description:
      'The OG 1× blended curve (Fyntrak history + live OG legs), levered 2.25×. Leverage model is '
      + 'admin-selectable: Fyntrak-linear (2.25× the 1× annualized — the default) or real compounded leverage.',
  },
  master_multi_account: {
    label: 'Master Multi Account',
    riskLabel: 'Combined trade journal · NQ',
    description: 'Master Multi Account — its own combined NQ trade-journal equity curve (fixed-notional on $100k), ingested from the account CSVs.',
  },
  mma_test: {
    label: 'MMA Test',
    riskLabel: 'Webhook feed test',
    description:
      'TEST strategy built live from the daily Fyntrak webhook feed: raw strategy orders are paired into '
      + 'completed trades (entry + tp/sl/exit) exactly like Fyntrak\'s processor, then blended into one '
      + 'fixed-notional curve. For integration testing only — small sample, not a real strategy.',
  },
  nt8: {
    label: 'NT8',
    riskLabel: 'Live NinjaTrader 8 account',
    description:
      'Live realized P&L from your synced NinjaTrader 8 connector account. Choose which account in '
      + 'Settings — the curve uses the same Capital Base as other strategies.',
  },
  btc_test: {
    label: 'BTC Test',
    riskLabel: 'Live TradingView feed (temporary)',
    description:
      'TEMPORARY TEST strategy fed directly by TradingView strategy-order alerts (BTCUSD 1m) hitting RiskLo\'s '
      + 'webhook — no n8n, no Fyntrak. Orders are paired into trades and plotted as a live fixed-notional curve '
      + 'that grows as new alerts arrive. Remove once the integration is verified.',
  },
  jean_test: {
    label: 'Jean Test',
    riskLabel: 'Dev-only NT8 feed',
    description:
      'DEV-ONLY internal test strategy fed by NT8 Trade Alert webhooks at '
      + '/api/webhooks/nt8/jean-test (username + content). Entry and exit arrive as '
      + 'separate POSTs. Visible to Admin and Brand Admin on dev only.',
  },
  vector_capital: {
    label: 'Vector Capital',
    riskLabel: 'Vector multistrat · NQ',
    description: 'The Vector multistrat: Grass Fed Prime Beef + Tuesday Special + Thursday Special, blended at 1× NQ, live from your Vector sheets.',
  },
  vector_capital_journal: {
    label: 'Vector Capital Journal',
    riskLabel: 'Account trade journal · NQ/MNQ',
    description:
      'The Vector_Capital account\'s actual executed trade journal (NQ / MNQ / MES futures), built into a $100k '
      + 'equity curve from the first trade date — each trade\'s net P&L applied to the running balance '
      + '(fixed-notional, same model as Master Multi Account). Windowed views rebase to the capital base at the '
      + 'window start.',
  },
  vc_core_standard: {
    label: 'Kairos Equity Standard',
    riskLabel: 'Core equity curve',
    description: 'Kairos Equity Standard — its full equity curve, built live from your loaded equity-strategy data.',
  },
  vc_core_aggressive: {
    label: 'Kairos Equity Aggressive',
    riskLabel: 'Core equity curve',
    description: 'Kairos Equity Aggressive — its full equity curve, built live from your loaded equity-strategy data.',
  },
  custom: {
    label: 'Custom',
    riskLabel: 'Manual setup',
    description: 'Choose sleeves, products, weekdays, and size multipliers yourself.',
  },
};

// ─── Equity Portfolio presets ────────────────────────────────────────────────
// Blend the five single-stock equity-curve strategies (already loaded in
// vector_equity_strategies) into one scenario. Mirrors Fyntrak's "Equity
// Portfolio 1× / 2.25×": equal 20% allocation per stock, with a portfolio-wide
// leverage multiplier. Stocks are resolved at runtime from the available equity
// strategies by name (ticker or full-name aliases), so we never hardcode the
// exact DB strategy names.
export const EQUITY_PORTFOLIO_PRESET_ORDER = ['equity_portfolio_1x', 'equity_portfolio_225x'];

export const OG_EQUITY_PORTFOLIO_PRESET_ORDER = ['og_equity_portfolio_1x', 'og_equity_portfolio_225x'];

/** All blended equity portfolio presets (standard + OG). */
export const ALL_EQUITY_PORTFOLIO_PRESET_ORDER = [
  ...EQUITY_PORTFOLIO_PRESET_ORDER,
  ...OG_EQUITY_PORTFOLIO_PRESET_ORDER,
];

/** Canonical strategy name for the PYTH oracle TSLA feed (distinct from NASDAQ TSLA). */
export const TSLA_PYTH_STRATEGY_NAME = 'Vector Capital — TSLA - PYTH (1H Algo)';

/** Extract ticker symbol from a Vector Capital 1H algo strategy name. */
export function tickerFromEquityAlgoName(name) {
  const raw = String(name || '').trim();
  if (/Vector Capital — TSLA - PYTH \(1H Algo\)/i.test(raw)) return 'TSLA';
  const m = raw.match(/Vector Capital — ([A-Z]{2,5})\s*\(/i);
  return m ? m[1].toUpperCase() : null;
}

/** Individual algos in the catalog that match a portfolio leg ticker. */
export function algosForPortfolioLegTicker(ticker, catalog) {
  const slot = String(ticker || '').toUpperCase();
  return (Array.isArray(catalog) ? catalog : []).filter(
    (name) => tickerFromEquityAlgoName(name) === slot,
  );
}

/** Default algo pick for a leg when admin has not saved strategyName yet. */
export function defaultAlgoForPortfolioLeg(ticker, catalog, portfolioKey) {
  const options = algosForPortfolioLegTicker(ticker, catalog);
  if (!options.length) return '';
  const slot = String(ticker || '').toUpperCase();
  const preferPyth = String(portfolioKey || '').startsWith('og_') && slot === 'TSLA';
  if (preferPyth) {
    return options.find((n) => /pyth/i.test(n)) || options[0];
  }
  if (slot === 'TSLA') {
    return options.find((n) => !/pyth/i.test(n)) || options[0];
  }
  return options[0];
}

/** Fyntrak STRATEGY_CONFIG / equityPerTrade defaults for portfolio legs. */
export const PORTFOLIO_LEG_DEFAULT_CAPITAL = {
  AAPL: 100000,
  TSLA: 10000,
  NVDA: 10000,
  AMZN: 10000,
  PLTR: 10000,
};

const EQUITY_PORTFOLIO_LEGS = [
  { ticker: 'AAPL', aliases: ['aapl', 'apple'], weightPct: 20 },
  { ticker: 'TSLA', aliases: ['tsla', 'tesla'], weightPct: 20 },
  { ticker: 'NVDA', aliases: ['nvda', 'nvidia'], weightPct: 20 },
  { ticker: 'AMZN', aliases: ['amzn', 'amazon'], weightPct: 20 },
  { ticker: 'PLTR', aliases: ['pltr', 'palantir'], weightPct: 20 },
];

/** OG portfolios: equal 20% legs; TSLA resolves to the PYTH feed algo. */
const OG_EQUITY_PORTFOLIO_LEGS = [
  { ticker: 'AAPL', aliases: ['aapl', 'apple'], weightPct: 20, equityPerTrade: 100000 },
  { ticker: 'TSLA', aliases: ['tsla - pyth', 'pyth'], weightPct: 20, equityPerTrade: 10000, preferredStrategyName: TSLA_PYTH_STRATEGY_NAME },
  { ticker: 'NVDA', aliases: ['nvda', 'nvidia'], weightPct: 20, equityPerTrade: 10000 },
  { ticker: 'AMZN', aliases: ['amzn', 'amazon'], weightPct: 20, equityPerTrade: 10000 },
  { ticker: 'PLTR', aliases: ['pltr', 'palantir'], weightPct: 20, equityPerTrade: 10000 },
];

function isStockOgVariant(name) {
  return /—\s*OG\s*$/i.test(String(name || '')) || /\bOG\b/.test(String(name || '').toUpperCase());
}

function isStock90Variant(name) {
  const upper = String(name || '').toUpperCase();
  return /\b90\b|90%|_90/.test(upper);
}

/** Prefer 100% / _100 curves for portfolio views (Fyntrak PORTFOLIO_STRATEGY_TABLE parity). */
function pickPortfolioLegStrategy(leg, strategies, { preferOg = false } = {}) {
  const list = Array.isArray(strategies) ? strategies : [];
  const preferred = String(leg?.preferredStrategyName || '').trim();
  if (preferred) {
    const exact = list.find((s) => String(s?.name || '').trim() === preferred);
    if (exact) return exact;
  }
  let best = null;
  let bestScore = -1;
  list.forEach((s) => {
    const name = String(s?.name || '');
    const lower = name.toLowerCase();
    if (!leg.aliases.some((alias) => lower.includes(alias))) return;
    if (isStock90Variant(name)) return;
    if (preferOg) {
      if (!isStockOgVariant(name)) return;
    } else if (isStockOgVariant(name)) {
      return;
    }
    if (!preferred && lower.includes('pyth')) return;
    if (preferred && lower.includes('tsla') && !lower.includes('pyth')) return;
    let score = 10;
    const upper = name.toUpperCase();
    if (!preferOg && /\b100\b|100%|_100/.test(upper)) score += 5;
    if (preferOg) score += 5;
    if (score > bestScore) {
      bestScore = score;
      best = s;
    }
  });
  return best;
}

export const EQUITY_PORTFOLIO_PRESETS = {
  equity_portfolio_1x: { id: 'equity_portfolio_1x', label: 'Equity Portfolio 1×', leverage: 1.0, legs: EQUITY_PORTFOLIO_LEGS },
  equity_portfolio_225x: { id: 'equity_portfolio_225x', label: 'Equity Portfolio 2.25×', leverage: 2.25, legs: EQUITY_PORTFOLIO_LEGS },
  og_equity_portfolio_1x: { id: 'og_equity_portfolio_1x', label: 'OG Equity Portfolio 1×', leverage: 1.0, legs: OG_EQUITY_PORTFOLIO_LEGS },
  og_equity_portfolio_225x: { id: 'og_equity_portfolio_225x', label: 'OG Equity Portfolio 2.25×', leverage: 2.25, legs: OG_EQUITY_PORTFOLIO_LEGS },
};

export function isEquityPortfolioPreset(presetId) {
  return Object.prototype.hasOwnProperty.call(EQUITY_PORTFOLIO_PRESETS, presetId);
}

// Preset id ↔ admin-config storage key (Portfolio Settings).
export const PORTFOLIO_KEY_BY_PRESET = {
  equity_portfolio_1x: 'portfolio_1x',
  equity_portfolio_225x: 'portfolio_2_25x',
  og_equity_portfolio_1x: 'og_portfolio_1x',
  og_equity_portfolio_225x: 'og_portfolio_2_25x',
};

// ─── Calculation methods ─────────────────────────────────────────────────────
// Two independent axes drive every Equity Portfolio curve:
//   • WEIGHTING   — how each stock's share of the blend is set
//   • COMPOUNDING — whether profits are reinvested
// The four combinations are the selectable "methods". Each carries the flags the
// builder needs (weighting + compounded) AND the human-readable math shown in the
// admin Method tab, so the explanation can never drift from the computation.
export const DEFAULT_EQUITY_PORTFOLIO_METHOD = 'equal_compounded';

const EQUAL_WEIGHTING_DETAIL =
  'Every enabled stock gets the same share, set by its Allocation % × leverage — '
  + 'so at the default 20% each they all weigh 20% (1×) or 45% (2.25×), regardless of '
  + 'dollar size. All five algos pull on the blend equally, so the high-fliers '
  + '(PLTR/TSLA) carry the same weight as the steady names.';
const POSITION_WEIGHTING_DETAIL =
  'Each stock is weighted by its real dollar exposure — its Equity/Trade ÷ the total. '
  + 'AAPL ($100k) ≈ 71% of the book and the four $10k legs ≈ 7% each (×leverage). This '
  + 'caps the high-fliers (PLTR/TSLA) so a couple of explosive names can\'t dominate.';
const ADDITIVE_DETAIL =
  'Profits are NOT reinvested. Every trade is sized on a fixed $100k base and the '
  + 'weighted daily returns are simply summed — the curve = base × (1 + Σ of all '
  + 'weighted returns). A steady, leverage-linear read of the edge.';
const COMPOUNDED_DETAIL =
  'Profits ARE reinvested. Each day\'s weighted return is applied to the running '
  + 'balance, so winnings ride into the next trade and snowball — the curve = '
  + 'yesterday\'s value × (1 + today\'s weighted return). Grows much faster over time, '
  + 'and leverage compounds (2.25× grows faster than 2.25× the 1× curve).';

export const EQUITY_PORTFOLIO_METHODS = {
  equal_additive: {
    id: 'equal_additive',
    label: 'Equal-weight · additive',
    weighting: 'equal',
    compounded: false,
    tagline: 'All 5 stocks at 20% each, profits not reinvested. The most conservative read.',
    weightingDetail: EQUAL_WEIGHTING_DETAIL,
    compoundingDetail: ADDITIVE_DETAIL,
    formula: 'value(t) = $100k × (1 + Σ_{day ≤ t} Σ_i  wᵢ · rᵢ(day)),   wᵢ = leverage × allocationᵢ',
    example: '3 trades of +10% at 20% weight → +2% + 2% + 2% = +6% on the fixed $100k base.',
  },
  equal_compounded: {
    id: 'equal_compounded',
    label: 'Equal-weight · compounded',
    weighting: 'equal',
    compounded: true,
    tagline: 'All 5 stocks at 20% each, profits reinvested (snowball).',
    weightingDetail: EQUAL_WEIGHTING_DETAIL,
    compoundingDetail: COMPOUNDED_DETAIL,
    formula: 'value(t) = value(t−1) × (1 + Σ_i  wᵢ · rᵢ(t)),   wᵢ = leverage × allocationᵢ',
    example: '3 trades of +10% at 20% weight on a growing balance → 1.02 × 1.02 × 1.02 − 1 ≈ +6.12%.',
  },
  position_additive: {
    id: 'position_additive',
    label: 'Position-size · additive',
    weighting: 'position',
    compounded: false,
    tagline: 'Weighted by real dollar size (AAPL ~71%), profits not reinvested.',
    weightingDetail: POSITION_WEIGHTING_DETAIL,
    compoundingDetail: ADDITIVE_DETAIL,
    formula: 'value(t) = $100k × (1 + Σ_{day ≤ t} Σ_i  wᵢ · rᵢ(day)),   wᵢ = leverage × (equityPerTradeᵢ ÷ Σ equityPerTrade)',
    example: 'AAPL ~71% weight, PLTR ~7% — so a +10% PLTR trade only adds ~+0.7% to the book.',
  },
  position_compounded: {
    id: 'position_compounded',
    label: 'Position-size · compounded',
    weighting: 'position',
    compounded: true,
    tagline: 'Weighted by real dollar size (AAPL ~71%), profits reinvested.',
    weightingDetail: POSITION_WEIGHTING_DETAIL,
    compoundingDetail: COMPOUNDED_DETAIL,
    formula: 'value(t) = value(t−1) × (1 + Σ_i  wᵢ · rᵢ(t)),   wᵢ = leverage × (equityPerTradeᵢ ÷ Σ equityPerTrade)',
    example: 'AAPL ~71% weight on a growing balance — the steady big leg carries the snowball, outliers stay capped.',
  },
};

export const EQUITY_PORTFOLIO_METHOD_ORDER = [
  'equal_additive', 'equal_compounded', 'position_additive', 'position_compounded',
];

/** Resolve a stored method id to its definition (falls back to the default). */
export function resolveEquityPortfolioMethod(id) {
  return EQUITY_PORTFOLIO_METHODS[id] || EQUITY_PORTFOLIO_METHODS[DEFAULT_EQUITY_PORTFOLIO_METHOD];
}

// ─── Custom strategy start basis ─────────────────────────────────────────────
// How the Custom blend handles strategies whose data starts on different dates.
// Each strategy gets its weight % of the capital base as its own compartment;
// the axis here is what happens BEFORE a young strategy's data begins. Stored
// per-user (localStorage), selectable in the admin Method tab.
export const DEFAULT_CUSTOM_BLEND_START_BASIS = 'full_history';

export const CUSTOM_BLEND_START_BASES = {
  full_history: {
    id: 'full_history',
    label: 'Full history · cash until live',
    tagline: 'The original. Curve spans the oldest strategy\'s full history; young strategies sit in cash until their data starts.',
    detail:
      'The blend starts at the EARLIEST first-data date across your picks. Each '
      + 'strategy\'s slice (weight % × capital base) compounds on its own returns '
      + 'from the day its data begins — before that, the slice sits flat as cash. '
      + 'Honest "what would this split have done since the beginning" read, but a '
      + 'young strategy dilutes Total/Annualized: its slice earns nothing for the '
      + 'years it didn\'t exist yet.',
    formula: 'equity(t) = cash + Σᵢ  sliceᵢ(t),   sliceᵢ(t) = wᵢ·base × Π_{day ≤ t, day ≥ startᵢ} (1 + rᵢ(day))',
    example:
      '33/33/34 across strategies starting 2020, 2022 and Feb 2026 → the curve runs '
      + 'from 2020, but the 34% slice is flat cash until Feb 2026 — dragging the '
      + 'blend\'s annualized far below the three strategies\' own numbers.',
  },
  common_start: {
    id: 'common_start',
    label: 'Common start date',
    tagline: 'Curve starts where ALL selected strategies have data. Every dollar works from day one.',
    detail:
      'The blend is clipped to the LATEST first-data date across your picks — the '
      + 'first day every selected strategy is live. All slices compound from that '
      + 'date, so Total/Annualized are a clean apples-to-apples blend of the '
      + 'strategies over their shared window. The trade-off: adding a young '
      + 'strategy shortens the whole strategy\'s visible history to its age.',
    formula: 'start = maxᵢ(startᵢ);   equity(t ≥ start) = cash + Σᵢ  wᵢ·base × Π_{start ≤ day ≤ t} (1 + rᵢ(day))',
    example:
      '33/33/34 across strategies starting 2020, 2022 and Feb 2026 → the curve '
      + 'starts Feb 2026 and every metric reflects only the months all three '
      + 'actually traded together.',
  },
  renormalized: {
    id: 'renormalized',
    label: 'Renormalized weights',
    tagline: 'Full history, no cash drag: live strategies share a young strategy\'s weight until it comes online.',
    detail:
      'The blend spans the full history (earliest start), but a strategy\'s weight '
      + 'is only "reserved" once its data begins. Before then, its weight is '
      + 'redistributed pro-rata across the strategies that ARE live, so the whole '
      + 'allocated balance is always working. The day a strategy comes online, the '
      + 'entire pot rebalances once to your target weights. Longest history without '
      + 'cash dilution — but early years reflect a different mix than you set.',
    formula: 'active weights: ŵᵢ(t) = wᵢ ÷ Σ_{live j} wⱼ × Σⱼ wⱼ;   pot rebalances to ŵ on each strategy\'s start date',
    example:
      '33/33/34 with the 34% strategy starting Feb 2026 → 2020–2026 runs the two '
      + 'live strategies at 50/50; on the young strategy\'s first day the balance '
      + 'rebalances to 33/33/34 and continues.',
  },
};

export const CUSTOM_BLEND_START_BASIS_ORDER = ['full_history', 'common_start', 'renormalized'];

/** Resolve a stored start-basis id to its definition (falls back to the default). */
export function resolveCustomBlendStartBasis(id) {
  return CUSTOM_BLEND_START_BASES[id] || CUSTOM_BLEND_START_BASES[DEFAULT_CUSTOM_BLEND_START_BASIS];
}

// ─── Leverage model ──────────────────────────────────────────────────────────
// A SECOND global axis, independent of the method above, that only affects the
// leveraged (2.25×) portfolios — the 1× books are identical under either model.
//   • 'compounded' — REAL leverage: multiply each day's blended return by the
//                    leverage and compound it. Mathematically honest; the curve
//                    grows faster than 2.25× the 1× annualized because leverage
//                    compounds on itself (~2.7× the 1× CAGR over a multi-year span).
//   • 'linear'     — FYNTRAK's model: the 2.25× annualized = 2.25 × the 1×
//                    annualized (a softened linear scaling, not true compounding
//                    leverage). Reproduced by re-scaling the 1× curve to that CAGR.
// Default is 'linear' so RiskLo's leveraged numbers line up with Fyntrak out of
// the box; switch to 'compounded' for the rigorous, real-leverage track record.
export const DEFAULT_EQUITY_PORTFOLIO_LEVERAGE_MODE = 'linear';

export const EQUITY_PORTFOLIO_LEVERAGE_MODES = {
  linear: {
    id: 'linear',
    label: 'Fyntrak linear leverage',
    tagline: 'Leveraged return = leverage × the 1× annualized. Matches Fyntrak.',
    detail:
      'The leveraged (2.25×) annualized return is set to exactly leverage × the 1× '
      + 'annualized — so 2.25× of a 41% 1× book reads ~92%, not the ~114% real '
      + 'compounding leverage would give. This mirrors how Fyntrak derives its 2.25× '
      + 'headline: a softened, linear scaling of the 1× curve, not a genuine '
      + 'compounded-leverage track record. The 1× books are unchanged.',
    formula: 'value(t) = base × (value₁ₓ(t) ÷ base) ^ s,   s = ln(1 + L·a) ÷ ln(1 + a),   a = 1× CAGR',
    example: 'OG 1× ≈ +41.5% annual → OG 2.25× ≈ 2.25 × 41.5% ≈ +93% (vs +114% under real leverage).',
  },
  compounded: {
    id: 'compounded',
    label: 'Real compounded leverage',
    tagline: 'Lever each day\'s return and compound it. Mathematically correct, runs hotter.',
    detail:
      'Each day\'s blended return is multiplied by the leverage and compounded into '
      + 'the running balance, so winnings ride into the next trade at full leverage. '
      + 'This is the honest leveraged result — but because leverage compounds on '
      + 'itself, a 2.25× curve grows to ~2.7× the 1× annualized over a multi-year '
      + 'span (e.g. 41% → ~114%, not ~92%). Will NOT match Fyntrak\'s softer figure.',
    formula: 'value(t) = value(t−1) × (1 + leverage · Σᵢ wᵢ · rᵢ(t))',
    example: 'OG 1× ≈ +41.5% annual → OG 2.25× ≈ +114% annual (leverage compounds beyond 2.25×).',
  },
};

export const EQUITY_PORTFOLIO_LEVERAGE_MODE_ORDER = ['linear', 'compounded'];

/** Resolve a stored leverage-mode id to its definition (falls back to the default). */
export function resolveEquityPortfolioLeverageMode(id) {
  return EQUITY_PORTFOLIO_LEVERAGE_MODES[id]
    || EQUITY_PORTFOLIO_LEVERAGE_MODES[DEFAULT_EQUITY_PORTFOLIO_LEVERAGE_MODE];
}

// Strategies whose stored curve is a real COMPOUNDING equity curve (an actual
// growing account balance, e.g. the Kairos QLD/TQQQ curves), as opposed to the
// fixed-notional strategy curves (Vector stocks, Master Multi Account, the equity
// blends) that sit on a flat $100k notional. For these, windowed/monthly returns
// must compound (end ÷ start − 1 vs the balance at the window start) — the same
// basis as their Total / Annualized metrics — instead of dividing each window's
// $ P&L by the original $100k (which over-states the return by the curve's growth
// factor once the balance has multiplied).
const COMPOUNDING_CURVE_SCENARIOS = new Set(['vc_core_standard', 'vc_core_aggressive']);

export function scenarioUsesCompoundMonthly(scenarioId) {
  return COMPOUNDING_CURVE_SCENARIOS.has(scenarioId);
}

/** Fixed-notional base for calendar-month % (all Vector strategies, including Kairos). */
export function monthlyBaseCapitalFor(_scenarioId, capitalBase) {
  const base = Number(capitalBase);
  return Number.isFinite(base) && base > 0 ? base : 100000;
}

/**
 * Resolve a portfolio preset's legs against the available equity strategies.
 * Returns one entry per leg with the matched strategy name (or null when the
 * stock isn't present). Defaults to the fixed 20% weighting, but an admin
 * `legConfig` ([{ ticker, enabled, allocation }]) overrides per-leg weight and
 * can disable a leg (disabled → found:false so the blend drops it).
 */
export function resolveEquityPortfolioLegs(presetId, equityStrategies, legConfig = null) {
  const preset = EQUITY_PORTFOLIO_PRESETS[presetId];
  if (!preset) return [];
  const list = Array.isArray(equityStrategies) ? equityStrategies : [];
  const cfgByTicker = Array.isArray(legConfig)
    ? new Map(legConfig.map((l) => [String(l?.ticker || '').toUpperCase(), l]))
    : null;
  return preset.legs.map((leg) => {
    const override = cfgByTicker ? cfgByTicker.get(leg.ticker) : null;
    const enabled = override ? override.enabled !== false : true;
    const weightPct = override && Number.isFinite(Number(override.allocation))
      ? Number(override.allocation)
      : leg.weightPct;
    const equityPerTrade = override && Number.isFinite(Number(override.equityPerTrade))
      ? Number(override.equityPerTrade)
      : (Number.isFinite(Number(leg.equityPerTrade))
        ? Number(leg.equityPerTrade)
        : (PORTFOLIO_LEG_DEFAULT_CAPITAL[leg.ticker] || 10000));
    const configuredStrategy = override?.strategyName
      ? String(override.strategyName).trim()
      : '';
    let match = null;
    if (configuredStrategy) {
      match = list.find((s) => String(s?.name || '').trim() === configuredStrategy) || null;
    }
    if (!match) {
      match = pickPortfolioLegStrategy(leg, list, { preferOg: presetId.startsWith('og_') });
    }
    return {
      ticker: leg.ticker,
      weightPct,
      equityPerTrade,
      strategyName: match?.name || null,
      found: Boolean(match) && enabled,
      enabled,
    };
  });
}

/**
 * Effective leverage for a portfolio preset = base leverage × equity%/100,
 * pulling overrides from the admin config map (keyed by portfolio_key) when
 * present. Mirrors Fyntrak's weight = (equity%/100) × leverage × allocation%.
 */
export function getEquityPortfolioEffectiveLeverage(presetId, configMap) {
  const preset = EQUITY_PORTFOLIO_PRESETS[presetId];
  const baseLeverage = preset ? preset.leverage : 1;
  const key = PORTFOLIO_KEY_BY_PRESET[presetId];
  const cfg = configMap && key ? configMap[key] : null;
  if (!cfg) return baseLeverage;
  const leverage = Number.isFinite(Number(cfg.leverage)) ? Number(cfg.leverage) : baseLeverage;
  const equityPct = Number.isFinite(Number(cfg.equityPct)) ? Number(cfg.equityPct) : 100;
  return leverage * (equityPct / 100);
}

const VECTOR_PRESET_TARGET_MICRO_UNITS_AT_250K = {
  conservative: 2,
  balanced: 5,
  aggressive: 10,
};

const VECTOR_PRESET_CAPITAL_BRACKETS = [100000, 250000, 500000, 1000000];

const EXECUTION_OPTIONS_BY_BASE = {
  NQ: [
    { id: 'NQ', label: 'NQ', ratio: 1 },
    { id: 'MNQ', label: 'MNQ', ratio: 0.1 },
  ],
  ES: [
    { id: 'ES', label: 'ES', ratio: 1 },
    { id: 'MES', label: 'MES', ratio: 0.1 },
  ],
  YM: [
    { id: 'YM', label: 'YM', ratio: 1 },
    { id: 'MYM', label: 'MYM', ratio: 0.1 },
  ],
  CL: [
    { id: 'CL', label: 'CL', ratio: 1 },
  ],
  GC: [
    { id: 'GC', label: 'GC', ratio: 1 },
  ],
};

const BASE_INSTRUMENT_OVERRIDES = {
  Abdo: 'ES',
  Avalanche: 'YM',
  'CL Night': 'CL',
  'ES 7AM': 'ES',
  'GC Night': 'GC',
  'GP 1': 'ES',
  'GP 2': 'ES',
  'Sokrat ES': 'ES',
  'YM Early': 'YM',
  'YM JNF': 'YM',
  'YM MCF': 'YM',
  'YM STL': 'YM',
};

const ACTIVE_DAY_OVERRIDES = {
  'Grass Fed Prime Beef (Current version 2.1C)': ['monday', 'wednesday', 'friday'],
  'Tuesday Special (Previous 2.2A)': ['tuesday'],
  'Thursday Special': ['thursday'],
};

const VECTOR_DEFAULT_PRESET_SLEEVES = [
  'Grass Fed Prime Beef (Current version 2.1C)',
  'Tuesday Special (Previous 2.2A)',
  'Thursday Special',
];

function normalizeCapitalBase(capitalBase) {
  const n = Number(capitalBase);
  if (!Number.isFinite(n) || n <= 0) return 250000;
  return Math.min(Math.max(n, 1), 1e12);
}

function formatTrimmedNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '1';
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(2).replace(/\.?0+$/, '');
}

function getScaledMicroUnits(presetId, capitalBase) {
  const anchorUnits = VECTOR_PRESET_TARGET_MICRO_UNITS_AT_250K[presetId] || 1;
  const scaled = (getVectorPresetCapitalBucket(capitalBase).value / 250000) * anchorUnits;
  return Math.max(1, Math.round(scaled));
}

export function getVectorPresetCapitalBucket(capitalBase) {
  const normalized = normalizeCapitalBase(capitalBase);
  const bucketValue =
    normalized >= 1000000
      ? 1000000
      : VECTOR_PRESET_CAPITAL_BRACKETS.reduce((acc, value) => (value <= normalized ? value : acc), 100000);

  return {
    value: bucketValue,
    label: `$${bucketValue.toLocaleString('en-US')}`,
    isStandard: true,
    rangeLabel:
      bucketValue >= 1000000
        ? '$1,000,000+'
        : bucketValue === 500000
          ? '$500,000 to $999,999'
          : bucketValue === 250000
            ? '$250,000 to $499,999'
            : '$1 to $249,999',
  };
}

export function getVectorPresetRecipe(presetId, capitalBase) {
  const microUnits = getScaledMicroUnits(presetId, capitalBase);
  if (microUnits >= 10 && microUnits % 10 === 0) {
    return {
      capitalBase: normalizeCapitalBase(capitalBase),
      microUnits,
      executionInstrument: 'NQ',
      sizeMultiplier: formatTrimmedNumber(microUnits / 10),
      displayLabel: `${formatTrimmedNumber(microUnits / 10)}x NQ`,
      microEquivalentLabel: `${microUnits}x MNQ equivalent`,
    };
  }

  return {
    capitalBase: normalizeCapitalBase(capitalBase),
    microUnits,
    executionInstrument: 'MNQ',
    sizeMultiplier: formatTrimmedNumber(microUnits),
    displayLabel: `${formatTrimmedNumber(microUnits)}x MNQ`,
    microEquivalentLabel: `${microUnits}x MNQ equivalent`,
  };
}

export function normalizeVcDays(days) {
  const set = new Set(
    Array.isArray(days)
      ? days
        .map((day) => String(day || '').trim().toLowerCase())
        .filter((day) => VC_DAY_SEQUENCE.includes(day))
      : [],
  );
  return VC_DAY_SEQUENCE.filter((day) => set.has(day));
}

export function getStrategyBaseInstrument(strategyName) {
  return BASE_INSTRUMENT_OVERRIDES[strategyName] || 'NQ';
}

export function getExecutionOptionsForBaseInstrument(baseInstrument) {
  return EXECUTION_OPTIONS_BY_BASE[baseInstrument] || EXECUTION_OPTIONS_BY_BASE.NQ;
}

export function getExecutionRatio(baseInstrument, executionInstrument) {
  const match = getExecutionOptionsForBaseInstrument(baseInstrument)
    .find((option) => option.id === executionInstrument);
  return match?.ratio || 1;
}

export function getStrategyScenarioDefaults(strategyName) {
  const baseInstrument = getStrategyBaseInstrument(strategyName);
  const executionOptions = getExecutionOptionsForBaseInstrument(baseInstrument);
  return {
    baseInstrument,
    executionOptions,
    defaultExecutionInstrument: executionOptions[0]?.id || baseInstrument,
    defaultActiveDays: normalizeVcDays(ACTIVE_DAY_OVERRIDES[strategyName] || VC_DAY_SEQUENCE),
  };
}

export function buildPresetPrograms(availableStrategyNames, capitalBase = 250000) {
  const available = new Set(Array.isArray(availableStrategyNames) ? availableStrategyNames : []);
  return Object.fromEntries(
    VECTOR_PRESET_ORDER.map((presetId) => [
      presetId,
      VECTOR_DEFAULT_PRESET_SLEEVES
        .map((strategyName) => {
          const recipe = getVectorPresetRecipe(presetId, capitalBase);
          return {
            strategyName,
            executionInstrument: recipe.executionInstrument,
            sizeMultiplier: recipe.sizeMultiplier,
          };
        })
        .filter((sleeve) => available.has(sleeve.strategyName))
        .map((sleeve) => {
          const defaults = getStrategyScenarioDefaults(sleeve.strategyName);
          return {
            strategyName: sleeve.strategyName,
            baseInstrument: defaults.baseInstrument,
            executionInstrument: sleeve.executionInstrument || defaults.defaultExecutionInstrument,
            activeDays: normalizeVcDays(sleeve.activeDays || defaults.defaultActiveDays),
            baseUnits: typeof sleeve.baseUnits === 'number' && sleeve.baseUnits > 0 ? sleeve.baseUnits : 1,
            sizeMultiplier:
              sleeve.sizeMultiplier != null && String(sleeve.sizeMultiplier).trim() !== ''
                ? String(sleeve.sizeMultiplier)
                : '1',
          };
        }),
    ]),
  );
}
